<script setup lang="ts">
import type { Product } from '@/lib/types/Product'
import Button from '@/components/Button.vue'
import Select from '@/components/form-fields/Select.vue'
import CheckIcon from '@/icons/status-success-outline.svg?component'
import QuantityControl from '@/components/QuantityControl.vue'
import formatMoney from '@/lib/formatters/money'
import { computed } from 'vue'

const props = defineProps<{
  productSelection: {
    product: Product
    selectedVariantId: string
    selectedSellingPlanId: string | undefined
    quantity: number
    selected: boolean
  }
}>()

const emit = defineEmits<{
  (e: 'toggleSubscribe'): void
  (e: 'updateQuantity', quantity: number): void
  (e: 'updateSellingPlan', sellingPlanId: string): void
}>()

const selectedVariant = computed(
  () =>
    props.productSelection.product.variants[
      props.productSelection.selectedVariantId
    ],
)

const price = computed(() => {
  const selectedSellingPlan = selectedVariant.value.sellingPlanAllocations.find(
    (spa) =>
      `${spa.sellingPlan.id}` === props.productSelection.selectedSellingPlanId,
  )
  if (selectedSellingPlan) {
    return (
      parseInt(selectedVariant.value.price) *
      ((100 - selectedSellingPlan.sellingPlan.price_adjustments[0].value) / 100)
    )
  } else {
    return parseInt(selectedVariant.value.price)
  }
})
</script>

<template>
  <div class="CartUpsellUpgradeItem Stack Stack--tiny">
    <div class="CartUpsellUpgradeItem__Product">
      <div class="CartUpsellUpgradeItem__Product__Image">
        <img
          :src="productSelection.product.image"
          :alt="productSelection.product.title"
        />
      </div>
      <div class="CartUpsellUpgradeItem__Product__Content">
        <p>
          <span>{{ productSelection.product.title }}</span>
        </p>
        <div class="CartUpsellUpgradeItem__Product__Inputs">
          <QuantityControl
            :value="productSelection.quantity"
            :min-value="1"
            :max-value="10"
            :is-busy="false"
            @change-quantity="emit('updateQuantity', $event)"
          ></QuantityControl>
          <Select
            v-if="selectedVariant.sellingPlanAllocations.length > 0"
            :value="productSelection.selectedSellingPlanId"
            @change="emit('updateSellingPlan', $event)"
          >
            <option
              v-for="option in selectedVariant.sellingPlanAllocations"
              :value="option.sellingPlan.id"
              :key="option.sellingPlanGroupId"
            >
              {{
                option.sellingPlan.name
                  .replace(/^delivery /i, '')
                  .replace('1 ', '')
                  .toLowerCase()
              }}
            </option>
          </Select>
        </div>
      </div>
    </div>
    <div class="CartUpsellUpgradeItem__Actions">
      <Button
        :modifiers="['block', 'inverted']"
        v-if="!productSelection.selected"
        @click="emit('toggleSubscribe')"
      >
        <template v-if="parseInt(selectedVariant.price) - price > 0">
          Subscribe and save
          {{ formatMoney(parseInt(selectedVariant.price) - price) }}
        </template>
        <template v-else>Subscribe</template>
      </Button>
      <div class="CartUpsellUpgradeItem__Actions__SubscribeMessage" v-else>
        <CheckIcon />
        <span>You're subscribed</span>
        <Button @click="emit('toggleSubscribe')" :modifiers="['link']">
          Undo
        </Button>
      </div>
    </div>
  </div>
</template>

<style>
.CartUpsellUpgradeItem {
  margin: 20px -24px 0;
  padding: 0 24px 20px;
  border-bottom: 1px solid var(--color-lichen-100);
}

.CartUpsellUpgradeItem__Product {
  display: flex;
  gap: 16px;
}

.CartUpsellUpgradeItem__Product__Image {
  width: 80px;

  & img {
    border-radius: var(--radius-small);
  }
}

.CartUpsellUpgradeItem__Product__Content {
  flex-grow: 1;
}

.CartUpsellUpgradeItem__Product__Inputs {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  & .QuantityControl {
    flex-grow: 0;
  }

  & .FormField {
    flex-grow: 1;
  }

  & .FormField__Input.FormField__Input--select {
    font-size: 16px;
    padding: 6px 10px;
  }
}

.CartUpsellUpgradeItem__Actions__SubscribeMessage {
  background-color: var(--color-lichen-100);
  font-weight: bold;
  padding: 12px 10px;
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  & span {
    flex-grow: 1;
  }
}
</style>
